import React, { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { PillButton, SurfaceCard } from "../../components/ui";
import { RegistrationDraftStore } from "../../store/store";
import { getUserData } from "../../API/firebase";
import { paths } from "../paths";

type OwnerChoice = "own" | "other";

export default function OwnerStep() {
  const navigate = useNavigate();
  const setOwnerCompanyName = RegistrationDraftStore(
    (s) => s.setOwnerCompanyName
  );

  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState<string>("");
  const [choice, setChoice] = useState<OwnerChoice>("own");
  const [organizationName, setOrganizationName] = useState("");

  useEffect(() => {
    let aborted = false;
    getUserData()
      .then((data) => {
        if (aborted) return;
        const value = data?.company?.trim() ?? "";
        setCompany(value);
        // Without a company on the profile, "own organization" cannot be used.
        if (!value) setChoice("other");
      })
      .catch(() => {
        if (!aborted) setChoice("other");
      })
      .finally(() => {
        if (!aborted) setLoading(false);
      });
    return () => {
      aborted = true;
    };
  }, []);

  const resolvedOwner =
    choice === "own" ? company : organizationName.trim();
  const canContinue = resolvedOwner.length > 0;

  if (loading) {
    return (
      <SurfaceCard>
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      </SurfaceCard>
    );
  }

  return (
    <SurfaceCard>
      <Typography variant="h4" sx={{ mb: 1 }}>
        Who owns this device?
      </Typography>
      <Typography variant="body2" sx={{ mb: 3 }}>
        The warranty is registered to the owner organization, not to the
        installer.
      </Typography>

      <FormControl sx={{ width: "100%" }}>
        <FormLabel id="owner-choice" sx={{ mb: 1 }}>
          Select the owner
        </FormLabel>
        <RadioGroup
          aria-labelledby="owner-choice"
          value={choice}
          onChange={(event) => setChoice(event.target.value as OwnerChoice)}
        >
          <FormControlLabel
            value="own"
            disabled={!company}
            control={<Radio />}
            label={
              company ? (
                <span>
                  I am part of the owner organization: <strong>{company}</strong>
                </span>
              ) : (
                <span>
                  I am part of the owner organization — no company on your
                  account.{" "}
                  <Link component={RouterLink} to={paths.account}>
                    Add one in Account
                  </Link>
                </span>
              )
            }
          />
          <FormControlLabel
            value="other"
            control={<Radio />}
            label="I am registering for another organization"
          />
        </RadioGroup>

        {choice === "other" && (
          <TextField
            label="Organization name"
            value={organizationName}
            onChange={(event) => setOrganizationName(event.target.value)}
            fullWidth
            margin="normal"
            autoFocus
          />
        )}
      </FormControl>

      {!company && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Your account has no company set, so enter the owner organization
          manually.
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end", mt: 3 }}>
        <PillButton
          tone="outline"
          onClick={() => navigate(paths.registerProjector)}
        >
          Back
        </PillButton>
        <PillButton
          tone="amber"
          withArrow
          disabled={!canContinue}
          onClick={() => {
            setOwnerCompanyName(resolvedOwner);
            navigate(paths.registerSummary);
          }}
        >
          Continue
        </PillButton>
      </Box>
    </SurfaceCard>
  );
}
